// Weekly Mirror — a premium weekly reading generated from the week's entries.
//
// Report and sections arrive sealed and are opened here with the DEK, the same as patterns and
// observations. `premium` is a flag rather than a 401 so a free account can still load the page and
// see what it would get; the archive is simply empty because generation never ran.

import { openWithKey } from '../crypto/crypto-vault';
import { apiGet } from './client';

export interface MirrorSections {
  emotionalWeather: string;
  whatKeptReturning: string[];
  dreamWaking: string;
  archetypeMovement: string[];
  contradiction: string;
  question: string;
  experiment: string;
  oneSentence: string;
  mayBeChanging: string;
  wantsToCheck: string;
}

export interface Mirror {
  id: string;
  weekStart: string;
  weekEnd: string;
  entryCount: number;
  /** Oreia's prose reading of the week, decrypted. */
  report: string;
  sections: MirrorSections | null;
  createdAt: string;
}

export interface MirrorArchive {
  isPremium: boolean;
  mirrors: Mirror[];
}

interface MirrorRow {
  id: string;
  week_start?: string | null;
  week_end?: string | null;
  entry_count?: number | null;
  report_cipher?: string | null;
  sections_cipher?: string | null;
  created_at?: string | null;
}

interface RawSections {
  emotional_weather?: string;
  what_kept_returning?: string[];
  dream_waking?: string;
  archetype_movement?: string[];
  contradiction?: string;
  question?: string;
  experiment?: string;
  one_sentence?: string;
  may_be_changing?: string;
  wants_to_check?: string;
}

async function open(dek: CryptoKey, cipher: string | null | undefined): Promise<string> {
  if (!cipher) return '';
  try {
    return await openWithKey(dek, cipher);
  } catch {
    // A single unreadable row must not blank the whole archive. This happens legitimately if a row
    // was written under a previous DEK, which is recoverable only by regenerating that week.
    return '';
  }
}

function toSections(raw: RawSections): MirrorSections {
  return {
    emotionalWeather: raw.emotional_weather ?? '',
    whatKeptReturning: raw.what_kept_returning ?? [],
    dreamWaking: raw.dream_waking ?? '',
    archetypeMovement: raw.archetype_movement ?? [],
    contradiction: raw.contradiction ?? '',
    question: raw.question ?? '',
    experiment: raw.experiment ?? '',
    oneSentence: raw.one_sentence ?? '',
    mayBeChanging: raw.may_be_changing ?? '',
    wantsToCheck: raw.wants_to_check ?? '',
  };
}

async function toMirror(dek: CryptoKey, row: MirrorRow): Promise<Mirror> {
  const sectionsJSON = await open(dek, row.sections_cipher);
  let sections: MirrorSections | null = null;
  if (sectionsJSON) {
    try {
      sections = toSections(JSON.parse(sectionsJSON) as RawSections);
    } catch {
      sections = null;
    }
  }

  return {
    id: row.id,
    weekStart: row.week_start ?? '',
    weekEnd: row.week_end ?? '',
    entryCount: Number(row.entry_count ?? 0),
    report: await open(dek, row.report_cipher),
    sections,
    createdAt: row.created_at ?? '',
  };
}

export async function fetchMirrors(dek: CryptoKey, signal?: AbortSignal): Promise<MirrorArchive> {
  const res = await apiGet<{ premium?: boolean; mirrors?: MirrorRow[] }>(
    '/api/weekly-mirror',
    signal,
  );
  return {
    isPremium: res.premium === true,
    mirrors: await Promise.all((res.mirrors ?? []).map((row) => toMirror(dek, row))),
  };
}
